import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { LoginCallback, SecureRoute, Security } from "@okta/okta-react";
import * as React from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { Container } from "semantic-ui-react";
import { config } from "../config";

import AdminFAQAdd from "../_components/admin/FAQAdd";
import AdminFAQDetail from "../_components/admin/FAQDetail";
import AdminFAQProducts from "../_components/admin/FAQDetailProducts";
import AdminFAQList from "../_components/admin/FAQList";
import Impersonate from "../_components/admin/Impersonate";
import ImpersonateRedirectByPolicy from "../_components/admin/ImpersonateRedirectByPolicy";
import ImpersonateRedirectByPolicyKey from "../_components/admin/ImpersonateRedirectByPolicyKey";
import IVRResult from "../_components/admin/IVRResult";
import AdminMarketingMessageAdd from "../_components/admin/MarketingMessageAdd";
import AdminMarketingMessageDetail from "../_components/admin/MarketingMessageDetail";
import AdminMarketingMessageDetailStates from "../_components/admin/MarketingMessageDetailStates";
import AdminMarketingMessageList from "../_components/admin/MarketingMessageList";
import Login from "../_components/auth/Login";
import Logout from "../_components/auth/Logout";
import Register from "../_components/auth/Register";
import LandingPage from "../_components/LandingPage";
import PolicyContainer from "../_components/policy/PolicyContainer";
import PolicyList from "../_components/policy/PolicyList";
import DocumentUpload from "../_components/service/DocumentUpload";
import ElectronicAuth from "../_components/service/ElectronicAuth";
import FormDownload from "../_components/service/FormDownload";
import Freelook from "../_components/service/Freelook";
import Surrender from "../_components/service/Surrender";

import Home from "../_components/Home";
import BeneficiaryChange from "../_components/service/BeneficiaryChange";
import Footer from "../_components/shared/Footer";
import TopNavbar from "../_components/shared/TopNavBar";
import { CPProvider } from "../_helpers/CPProvider";

import SettingAdd from "../_components/admin/SettingAdd";
import SettingDetail from "../_components/admin/SettingDetail";
import Settings from "../_components/admin/Settings";

import AddNewBankDraft from "_components/service/AddNewBankDraft";
import BankDraftReview from "_components/service/BankDraftReview";
import ChangeBankDraftAmount from "_components/service/ChangeBankDraftAmount";
import ChangeBankRoutingAccount from "_components/service/ChangeBankRoutingAccount";
import OneTimePayment from "_components/service/OneTimePayment";
import useSessionTimer from "_helpers/useSessionTimer";
import Maintenance from "../_components/Maintenance";
import BankDraftDateChange from "../_components/service/BankDraftDateChange";
import CompanyList from "../_components/service/CompanyList";
import DocumentDelivery from "../_components/service/DocumentDelivery";
import DocumentDeliveryAuthorization from "../_components/service/DocumentDeliveryAuthorization";
import Forms from "../_components/service/Forms";
import Messages from "../_components/service/Messages";
import RemotePayment from "../_components/service/RemotePayment";
import PolicySurrenderForm from "_components/service/PolicySurrender";

const oktaAuth = new OktaAuth(config.oidc);

interface AppProps {
    paperCompanyData: PaperCompany | null;
}

// eslint-disable-next-line
const App = ({ paperCompanyData }: AppProps) => {
    const navigate = useHistory();
    const sessionTimer = useSessionTimer(Number(config.app.userTimeout));
    const keepAlive = () => {
        const authState = oktaAuth.authStateManager.getAuthState();
        if (authState?.isAuthenticated) {
            sessionTimer.keepAlive();
        }
    }

    //Store initial request
    if (window.location.pathname.includes("callback")) {
    } else {
        localStorage.setItem("appLoadUri", window.location.pathname + window.location.search);
    }

    const triggerLogin = async () => {
        await oktaAuth.signInWithRedirect();
    };

    const triggerLogout = async () => {
        navigate.replace("/logout");
    };

    // eslint-disable-next-line
    const restoreOriginalUri = async (_oktaAuth: any, originalUri: string) => {
        const originalUriCustom = localStorage.getItem("appLoadUri");
        //Testing a custom Uri for redirecting user and ignoring the function loaded one
        originalUri = originalUriCustom ?? "/";
        console.log("originalUriCustom=" + originalUriCustom);
        console.log("restoreOriginalUri = " + originalUri);
        navigate.replace(toRelativeUrl(originalUri || "/", window.location.origin));
    };

    const customAuthHandler = async () => {
        const previousAuthState = oktaAuth.authStateManager.getPreviousAuthState();
        if (!previousAuthState || !previousAuthState.isAuthenticated) {
            await triggerLogin();
        } else {
            triggerLogout();
        }
    };

    React.useEffect(() => {
        window.addEventListener("click", keepAlive);
        return () => {
            window.removeEventListener("click", keepAlive);
        };
    }, []);

    const redirectComponent = (path: string) => {
        window.location.href = `${config.resourceServer.apiUrl}${path}`;
        return null;
    };

    return (
        <Security
            oktaAuth={oktaAuth}
            onAuthRequired={customAuthHandler}
            restoreOriginalUri={restoreOriginalUri}
        >
            <CPProvider initialPaperCompany={paperCompanyData}>
                <TopNavbar />
                <Container text>
                    <Switch>
                        <Route path="/" exact component={Home} />
                        <Route path="/maint" exact component={Maintenance} />
                        <Route path="/login/callback" component={LoginCallback} />
                        <Route path="/register" component={Register} />
                        <Route path="/login" component={Login} />
                        <Route path="/logout" component={Logout} />
                        <SecureRoute path="/landingpage" component={LandingPage}></SecureRoute>
                        <SecureRoute path="/policies" component={PolicyList}></SecureRoute>
                        <SecureRoute
                            path="/policy-detail/:id"
                            component={PolicyContainer}
                        ></SecureRoute>
                        {/* Service/Requests */}
                        <SecureRoute path="/freelook/:id" component={Freelook}></SecureRoute>
                        <SecureRoute path="/surrender/:id" component={Surrender}></SecureRoute>
                        <SecureRoute
                            path="/electronicauth/:id"
                            component={ElectronicAuth}
                        ></SecureRoute>
                        <SecureRoute
                            path="/document-upload/:id"
                            component={DocumentUpload}
                        ></SecureRoute>
                        <SecureRoute path="/freelook/:id" component={Freelook}></SecureRoute>
                        <SecureRoute
                            path="/surrender-form/:policyDWKey"
                            component={PolicySurrenderForm}
                        ></SecureRoute>
                        <SecureRoute
                            path="/formdownload/:policyDWKey"
                            component={FormDownload}
                        ></SecureRoute>
                        <SecureRoute
                            path="/beneficiary-change/:id"
                            component={BeneficiaryChange}
                        ></SecureRoute>
                        <SecureRoute
                            path="/bankdraft-date-change/:id"
                            component={BankDraftDateChange}
                        ></SecureRoute>
                        <SecureRoute
                            path="/payment/:id"
                            component={RemotePayment}
                        ></SecureRoute>
                        <SecureRoute path="/download-forms/:id" component={Forms}></SecureRoute>
                        <SecureRoute
                            path="/document-delivery"
                            component={DocumentDelivery}
                        ></SecureRoute>
                        <SecureRoute path="/messages" component={Messages}></SecureRoute>
                        <SecureRoute path="/company-list" component={CompanyList}></SecureRoute>
                        <SecureRoute
                            path="/document-delivery-authorization"
                            component={DocumentDeliveryAuthorization}
                        ></SecureRoute>
                        <SecureRoute
                            path="/payments/add-new-bank-draft/:id"
                            component={AddNewBankDraft}
                        ></SecureRoute>
                        <SecureRoute
                            path="/payments/change-bank-routing-account/:id"
                            component={ChangeBankRoutingAccount}
                        ></SecureRoute>
                        <SecureRoute
                            path="/payments/change-bank-draft-amount/:id"
                            component={ChangeBankDraftAmount}
                        ></SecureRoute>
                        <SecureRoute
                            path="/payments/one-time-payment/:id"
                            component={OneTimePayment}
                        ></SecureRoute>
                        <SecureRoute
                            path="/payments/bank-draft-review/:id/:request"
                            component={BankDraftReview}
                        ></SecureRoute>
                        {/* Admin Related */}
                        <SecureRoute path="/ivrresult/:id" component={IVRResult}></SecureRoute>
                        <SecureRoute
                            path="/impersonateredirectbypolicykey/:id"
                            component={ImpersonateRedirectByPolicyKey}
                        ></SecureRoute>
                        <SecureRoute
                            path="/impersonateredirectbypolicy/:id"
                            component={ImpersonateRedirectByPolicy}
                        ></SecureRoute>
                        <SecureRoute path="/impersonate/:id" component={Impersonate}></SecureRoute>
                        <SecureRoute path="/admin/faqs" component={AdminFAQList}></SecureRoute>
                        <SecureRoute path="/admin/faq/:id" component={AdminFAQDetail}></SecureRoute>
                        <SecureRoute path="/admin/faqadd" component={AdminFAQAdd}></SecureRoute>
                        <SecureRoute
                            path="/admin/faq-products/:id"
                            component={AdminFAQProducts}
                        ></SecureRoute>
                        <SecureRoute
                            path="/admin/marketing-messages"
                            component={AdminMarketingMessageList}
                        ></SecureRoute>
                        <SecureRoute
                            path="/admin/marketing-message/:id"
                            component={AdminMarketingMessageDetail}
                        ></SecureRoute>
                        <SecureRoute
                            path="/admin/marketing-message-add"
                            component={AdminMarketingMessageAdd}
                        ></SecureRoute>
                        <SecureRoute
                            path="/admin/marketing-message-states/:id"
                            component={AdminMarketingMessageDetailStates}
                        ></SecureRoute>
                        <SecureRoute path="/admin/settings" component={Settings}></SecureRoute>
                        <SecureRoute
                            path="/admin/settingdetail/:id"
                            component={SettingDetail}
                        ></SecureRoute>
                        <SecureRoute path="/admin/settingadd" component={SettingAdd}></SecureRoute>
                    </Switch>
                </Container>
                <Footer />
            </CPProvider>
        </Security>
    );
};

export default App;
